import React from 'react';
import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

/**
 * Wraps all admin-only routes (dashboard, bookings, etc.). Scanner-role users are
 * redirected to /scanner so they cannot open other pages even by direct URL.
 */
export function AdminOnlyOutlet() {
  const { user } = useAuth();
  if (user?.role === 'scanner') {
    return <Navigate to="/scanner" replace />;
  }
  return <Outlet />;
}

/** Only users with role scanner may open /scanner (admins are redirected home). */
export function ScannerRoleRoute({ children }) {
  const { user } = useAuth();
  if (user?.role !== 'scanner') {
    return <Navigate to="/" replace />;
  }
  return children;
}

export function ProtectedRoute({ children }) {
  const { authReady, isAuthenticated, mustChangePassword } = useAuth();
  const location = useLocation();

  if (!authReady) {
    return (
      <div className="auth-loading" style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100vh',
        background: '#f5f5f5',
        fontSize: '1rem',
        color: '#666',
      }}>
        Loading…
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  if (mustChangePassword) {
    return <Navigate to="/change-password" state={{ from: location }} replace />;
  }

  return children;
}

export function ChangePasswordOnlyRoute({ children }) {
  const { authReady, isAuthenticated, mustChangePassword } = useAuth();
  const location = useLocation();

  if (!authReady) {
    return (
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100vh',
        background: '#f5f5f5',
        fontSize: '1rem',
        color: '#666',
      }}>
        Loading…
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  if (!mustChangePassword) {
    return <Navigate to={location.state?.from?.pathname || '/'} replace />;
  }

  return children;
}

export function PublicLoginRoute({ children }) {
  const { authReady, isAuthenticated, mustChangePassword } = useAuth();
  const location = useLocation();

  if (!authReady) {
    return (
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100vh',
        background: '#f5f5f5',
        fontSize: '1rem',
        color: '#666',
      }}>
        Loading…
      </div>
    );
  }

  if (!isAuthenticated) return children;
  if (mustChangePassword) {
    return <Navigate to="/change-password" state={{ from: location }} replace />;
  }
  return <Navigate to={location.state?.from?.pathname || '/'} replace />;
}
